import type { MicroAppBindingData } from "@/api/generated/types.gen";
import { getAccessToken } from "@/api/tokens";

export type { MicroAppBindingData };

async function authedFetch(path: string, signal?: AbortSignal): Promise<Response> {
  const token = await getAccessToken();
  const res = await fetch(`${globalThis.location.origin}/api/v1${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal,
  });
  return res;
}

/**
 * Fetch a micro-app's HTML content. The `/micro-apps/{uuid}/content` endpoint
 * returns text/html (not the JSON envelope), so it doesn't go through the
 * generated client — this is the src/api seam for it, used by the viewer.
 */
export async function fetchMicroAppContent(uuid: string): Promise<string> {
  const res = await authedFetch(`/micro-apps/${uuid}/content`);
  if (!res.ok) throw new Error(`Couldn't load the app (${res.status})`);
  return res.text();
}

/**
 * Fetch one binding's live data for the viewer's hydration runtime. The backend serves a cached
 * value while it's fresh; `refresh` (the Refresh data button) fetches now instead.
 */
export async function fetchMicroAppBindingData(
  uuid: string,
  bindingKey: string,
  signal?: AbortSignal,
  refresh = false,
): Promise<MicroAppBindingData> {
  const query = refresh ? "?refresh=true" : "";
  const res = await authedFetch(`/micro-apps/${uuid}/data/${encodeURIComponent(bindingKey)}${query}`, signal);
  if (!res.ok) throw new Error(`Couldn't load binding ${bindingKey} (${res.status})`);
  const body = (await res.json()) as { data: MicroAppBindingData };
  return body.data;
}
